(ns ui.util
  (:refer-clojure :exclude [dosync])
  (:require ["/js/utils/currency" :as  currency]
            ["luxon"            :refer [DateTime]]
            ["moment"]
            ["moment-timezone"]
            [clojure.string     :as    str]
            [cljs.pprint        :refer (cl-format)]
            [inflections.core   :as    inf]
            [goog.string        :as    gstring]
            [cljs.core.async    :as    async :refer [>! chan alts!]]
            [javelin.core       :refer [cell cell? formula]])
  (:require-macros [javelin.core :refer [cell= formula-of with-let]]
                   [hoplon.core  :refer [with-timeout]]
                   [cljs.core.async.macros :refer [go]]))

(defn map-cell
  "Given a map `m` where the keys and/or values are cells, returns a cell whose
   value is a map (not containing any cells as keys/values).

   For example:

     (map-cell
       {:foo        (cell true)
        :bar        (cell 42)
        (cell :baz) [1 2 3]})

   Returns a cell whose immediate value is:

     {:foo true
      :bar 42
      :baz [1 2 3]}

   and the value is updated along with the individual cells inside of the map."
  [m]
  (apply
    (formula (fn [& ks-and-vs]
               (apply hash-map ks-and-vs)))
    (apply concat (seq m))))

(defn list-of-maps-cell
  "Given a list of `maps` where the keys and/or values are cells, returns a cell
   whose value is a list of maps (not containing any cells as keys/values).

   For example:

     (list-of-maps-cell
       [{:foo        (cell 1)
         :bar        (cell 2)
         (cell :baz) 3}
        {:foo        (cell 4)
         :bar        (cell 5)
         (cell :baz) 6}])

   Returns a cell whose immediate value is:

     [{:foo 1
       :bar 2
       :baz 3}
      {:foo 4
       :bar 5
       :baz 6}]

   and the value is updated along with the individual cells inside of the maps."
  [maps]
  (apply
    (formula (fn [& maps] maps))
    (map map-cell maps)))

(defn remove-nil-vals
  "Given a map `m`, returns a map with entries whose values (including
  nested) are nil removed."
  [m]
  (->> m
       (remove #(-> % val nil?))
       (map (fn [[k v]]
              (if (map? v)
                [k (remove-nil-vals v)]
                [k v])))
       (into {})))

(defn add-initfn!
  "Executes a function once the window load event is fired.

   Or, if the page has already loaded, executes the function immediately."
  [f]
  (let [f' #(with-timeout 0 (f))]
    (if (= "complete" (.-readyState js/document))
      (f')
      (.addEventListener js/window "load" f'))))

(def default-date-format "YYYY-MM-DD HH:mm")

(defn str=
  "Compares the string representations of args for equality."
  [& args]
  (apply = (map str args)))

(defn in-future? [dt]
  (.isAfter (js/moment. dt) (js/moment.)))

(defn yesterday
  "return yesterday's date starting from moment"
  []
  (-> (js/moment.) (.subtract 1 "day") (.startOf "day")  (.toDate)))

(defn start-of-day
  "get the datetime at start of given day or start of today for single arity version"
  ([] (start-of-day (js/moment.)))
  ([dt]
   (-> (js/moment. dt) (.startOf "day")  .toDate)))

(defn as-utc [dt]
  (-> js/moment (.utc dt) (.toDate)))

(defn utc-now [] (-> (js/moment.) .utc .toDate))

(defn start-of-month
  "get the first day of the current month or the first day of a previous month if months-ago is given."
  ([] 
   (-> (js/moment) (.startOf "month") (.toDate)))
  ([months-ago] 
   (-> (js/moment) (.subtract months-ago "months") (.startOf "month") (.toDate))))

(defn end-of-month
  "get the end day of the current month or the end day of a previous months-ago if months-ago is given"
  ([] 
   (-> (js/moment) (.endOf "month") (.toDate)))
  ([months-ago] 
   (-> (js/moment) (.subtract months-ago "months") (.endOf "month") (.toDate))))

(defn end-of-day
  "get the datetime at end of given day or end of today for single arity version"
  ([] (end-of-day (js/moment.)))
  ([dt]
   (-> (js/moment. dt) (.endOf "day") .toDate)))

(defn start-of-quarter
  "get the date of the first day of this quarter"
  []
  (-> (js/moment) (.startOf "quarter") (.toDate)))

(defn start-of-year
  "get the first date of the current year"
  []
  (-> (js/moment.) (.startOf "year") (.toDate)))

(defn days-ago
  "returns start of day date n days from today (or given date) in the past"
  ([n] (days-ago (js/moment.) n))
  ([dt n]
   (-> (js/moment.utc dt) (.subtract n "days") (.startOf "day") .toDate)))

(defn days-future
  "Returns start of day date n days from today (or given date) in the future"
  ([n] (days-future (js/moment.) n))
  ([dt n]
  (-> (js/moment.utc dt) (.add n "days") (.startOf "day") .toDate)))

(defn last-week
  "returns start and end of last week based on the given day."
  ([week-start]
   (let [weekday-offset (if (= week-start :sunday)
                          "week" 
                          "isoWeek")]  
     {:start (-> (js/moment) (.subtract 1 "week") (.startOf weekday-offset) .toDate)
      :end (-> (js/moment) (.subtract 1 "week") (.endOf weekday-offset) .toDate)})))

(defn hours-ago
  "Returns a datetime that is `n` hours before a datetime `dt`."
  ([dt n] (-> (js/moment. dt) (.subtract n "hours") .toDate)))

(defn safe-name [x] (when x (name x)))

(defn safe-apply
  [this method-name args]
  (.apply (aget this method-name) this (into-array (map clj->js args))))

(defn vec-move
  "Given a vector <coll>, move the item and index <from> to be at index <to>."
  [coll from to]
  (let [v1  (into (subvec coll 0 from) (subvec coll (inc from)))]
    (into (conj (subvec v1 0 to) (get coll from)) (subvec v1 to))))

(defn safe-deref
  "if thing is a cell deref it, otherwise just return it"
  [thing]
  (if (cell? thing) @thing thing))

(defn ensure-cell
  "Takes an arg and wraps it in a cell if it is not a already a cell"
  [arg]
  (if (cell? arg) arg (cell arg)))

(defn do-watch
  ([var f]
   (do-watch var nil f))
  ([var init f]
   (with-let [this var]
     (add-watch this (gensym)
       (fn [_ _ before after]
         (f before after)))
     (f init @var))))

(defn on-change
  ([x f]
   (on-change x not= f))
  ([x changed? f]
   (with-let [last (atom nil)]
     (do-watch x (fn [_ new]
                   (when (changed? @last new)
                     (reset! last new)
                     (f new)))))))

(defn on-value
  [x val f]
  (on-change x (fn [y] (when (= y val) (f)))))

(defn path-cell [c path]
  (cell= (get-in c path) (partial swap! c assoc-in path)))

(defn constrained-cell
  "Returns a cell that is a constrained version of a cell `c`.

   When `c` changes, if `(constraint-fn c)` is true, then the constrained
   version also changes.

   Otherwise, it retains the previous value."
  [c constraint-fn & [init-val]]
  (with-let [c' (cell init-val)]
    (do-watch
      c
      (fn [_ after]
        (when (constraint-fn after)
          (reset! c' after))))))

(defn first-value-cell
  "Returns an output cell whose value is the first non-nil value of
   `input-cell`.

   For example, if the value of the `input-cell` is initially nil, then the
   output cell's value will be nil until `input-cell` has a non-nil value, let's
   say 42. Then the output cell will be 42 forever, even if `input-cell`
   continues to change.

   Another example: if the value of `input-cell` is initially :foo, then the
   value of the output cell will be :foo forever, even if `input-cell` continues
   to change."
  [input-cell]
  (let [first-value (cell nil)]
    (formula-of [input-cell]
      (let [fv @first-value]
        (if (nil? fv)
          (reset! first-value input-cell)
          fv)))))

(defn swap-if!
  "Applies `update-fn` to the value within `input-cell`, provided
   `pred` holds of the value in `input-cell`. Otherwise applies
   the `default-fn`."
  [input-cell pred update-fn default-fn]
  (swap! input-cell
         #(if (pred %)
            (update-fn %)
            (default-fn %))))

(defn trigger
  "Takes a function of a single argument and returns an instance of a
  cljs.core/IReset that calls the function on -reset! This exists so
  that you can escape the cell world of castra and instead trigger callbacks."
  [f]
  (reify cljs.core/IReset (-reset! [_ x] (f x))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn format
  [& args]
  (apply gstring/format args))

(defn format-date
  [fmt date & [tz]]
  (let [tz  (if-not (empty? tz) tz "UTC")
        out (.format (.tz (js/moment date) tz) fmt)]
    (when-not (= out "Invalid date") out)))

(defn parse-utc-date
  "Parses a string into a Date, assuming UTC.

   Returns nil if the string cannot be parsed as a valid date-time."
  [fmt s]
  ;; The `true` argument here enables parsing in strict mode.
  ;; See: https://momentjs.com/guides/#/parsing/strict-mode/
  (let [m (.utc js/moment s fmt true)]
    (when (.isValid m)
      (.toDate m))))

(defn parse-timezone-date
  "Parses a string into a Date with the given time zone.

   Returns nil if the string cannot be parsed as a valid date-time."
  [fmt s tz]
  (when-not (empty? s)
    ;; The `true` argument here enables parsing in strict mode.
    ;; See: https://momentjs.com/guides/#/parsing/strict-mode/
    ;;      https://momentjs.com/timezone/docs/#/using-timezones/parsing-in-zone/
    (let [m  (.tz js/moment s fmt true tz)]
      (when (.isValid m)
        (.toDate m)))))

(defn- split-amount
  "Splits the absolute value of `n` into its whole part and its fraction in
  `places` digits, as the integers [whole frac]. `truncation` is #{:truncate
  :round}. The sign of `n` is the caller's business."
  [n truncation places]
  (let [whole  (.abs js/Math (long n))
        factor (.pow js/Math 10 places)
        frac   (condp  = truncation
                 :truncate   (-> (* 10 factor (.abs js/Math n))
                                 (- (* 10 factor whole))
                                 (->> (.round js/Math))
                                 (/ 10)
                                 long)

                 :round      (-> (.abs js/Math n)
                                 (- whole)
                                 (* factor)
                                 (->> (.round js/Math))))]
    ;; Need to check for numbers that have rounded up
    ;; where the fraction is equivalent to the factor
    ;; to prevent a whole number being converted to a 10th fraction
    ;; E.g. 9.9999 => 10.00000 => $10.00
    ;; rather than 9.9999 => 9.10000 => $9.10 (see sc-13909)
    (if (= frac factor)
      [(inc whole) 0]
      [whole frac])))

(defn- format-usd
  "Formats a number as US dollars the way the UI always has: en-US style for
  every viewer, whatever their browser locale. Used when the network sets no
  currency. See `format-currency` for the options."
  [n truncation places]
  (let [template     (str "~[-~;~]$~:D.~" places "'0d")
        sign         (if (neg? n) 0 1)
        [whole frac] (split-amount n truncation places)
        value        (cl-format nil template sign whole frac)]

    ;; {:places 3} is used for the "Prices" column on the flights page
    ;; Originally implemented for Coupang due to extreme exchange rate
    ;; between the USD and South Korean Won.
    (if (and (= (last value) "0") (= places 3))
      ;; remove trailing third 0
      (subs value 0 (dec (count value)))
      value)))

(defn format-currency
  "Formats a number as a money amount. Display only: the amount is never
  converted.

  When the network sets a currency (or `:currency` is passed), it is formatted
  in that currency with `Intl.NumberFormat` in the browser locale, so
  separators, symbol position and fraction digits follow the locale and the
  currency. When it sets none, it is formatted as US dollars in en-US style
  for every viewer, exactly as before network currencies.

  Takes the following options:
    :truncation => #{:truncate :round}
    :places     => 2 or 3. 2 shows the currency's own fraction digits (2 for
                   USD, 0 for JPY); 3 shows up to one more, dropping a trailing
                   zero
    :currency   => ISO 4217 code; defaults to the network currency, which is
                   also used when the code is not a valid currency
    :locale     => BCP 47 locale; defaults to the browser locale. Ignored when
                   there is no currency"
  [n & [{:keys [truncation places currency locale] :or {truncation :truncate places 2}}]]
  (assert (#{2 3} places) "Only supports 2 or 3 decimal places")
  (assert (#{:truncate :round} truncation) "truncation must be one of #{:truncate :round}")
  (when n
    (if-let [currency (or (currency/normalizeCurrency currency) (currency/getNetworkCurrency))]
      (let [;; {:places 3} is used for the "Prices" column on the flights page
            ;; Originally implemented for Coupang due to extreme exchange rate
            ;; between the USD and South Korean Won.
            min-places   (currency/getCurrencyFractionDigits currency)
            max-places   (+ min-places (- places 2))
            [whole frac] (split-amount n truncation max-places)
            amount       (+ whole (/ frac (.pow js/Math 10 max-places)))]
        ;; A negative amount that truncates to zero keeps its sign ("-$0.00"),
        ;; because (- 0) is -0, which Intl formats with a minus sign.
        (currency/formatCurrency (if (neg? n) (- amount) amount)
                                 #js {:currency              currency
                                      :locale                locale
                                      :minimumFractionDigits min-places
                                      :maximumFractionDigits max-places}))
      (format-usd n truncation places))))

(defn format-number
  "Formats integers with commas.

   e.g. (format-number 1000000) => \"1,000,000\""
  [n]
  (cl-format nil "~:d" n))

(defn format-number-truncate
  [n]
  (let [formatter (js/Intl.NumberFormat "en-US" (clj->js {:compactDisplay        "short"
                                                          :maximumFractionDigits 2
                                                          :minimumFractionDigits 2
                                                          :notation              "compact"
                                                          :trailingZeroDisplay    "stripIfInteger"}))]
    (-> (.format formatter n)
        str/lower-case)))

(defn indent
  "Indents each line of a string `s` `level` levels.

   If `level` is not provided, indents by one level."
  ([s]
   (indent 1 s))
  ([level s]
   (let [SPACES 2
         init-ws (apply str (repeat (* level SPACES) \space))]
     (-> s
         (str/split #"\n")
         (#(map (partial str init-ws) %))
         (#(str/join "\n" %))))))

(defn not-NaN
  [x]
  (when-not (js/isNaN x) x))

(defn pct [x y]
  (when-let [z (not-NaN (* 100 (/ x y)))] (format "%.2f" z)))

(defn ->number
  "Parse `x` into a number, returning nil if unable."
  [x]
  (cond
    (number? x)
    x

    (and (string? x) (seq x))
    (let [v (-> x js/Number. .valueOf)]
      (when-not (js/isNaN v)
        v))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn singularize
  [word]
  (when word (inf/singular word)))

(defn title-case
  "Capitalize every word in a string"
  [s]
  (when s
    (->> (str/split (str s) #"\b")
         (map str/capitalize)
         (str/join))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn clamp
  [lower upper n]
  (-> n (min upper) (max lower)))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn buffered-handler
  "Keep accepting updates, fire event handler when timeout has
  expired. Used e.g. when doing typeahead and you don't want to make a
  server request for every keystroke."
  [timeout-length handler]
  (let [val-chan (chan)
        timeout  (atom (chan))
        buffer   (atom nil)]
    (go (while true
          (let [[val c] (alts! [val-chan @timeout])]
            (if (= c val-chan)
              (reset! buffer val)
              (do (handler @buffer)
                  (reset! timeout (chan)))))))
    (fn [val]
      (when (not= val @buffer)
        (reset! timeout (async/timeout timeout-length))
        (go (>! val-chan val))))))

(def ^:const DEFAULT-DEBOUNCE-MS 300)

(defn debounce
  [timeout f]
  (let [id (atom nil)]
    (fn [event]
     (if (zero? timeout)
      (f event)
      (do (js/clearTimeout @id)
          (reset! id (js/setTimeout
                      (partial f event)
                      timeout)))))))

(defn event-target-value
  [e]
  (-> e .-target .-value))

(defn select-text
  "Select the text w/in an element simulating CTRL-A."
  [css-id]
  (let [doc  js/document
        text (.getElementById doc css-id)]
    (if-let [selection (and (.-getSelection js/window)
                            (.getSelection  js/window))]
      ;; Webkit / Newer Browsers
      (let [range (.createRange doc)]
        (.selectNodeContents range     text)
        (.removeAllRanges    selection)
        (.addRange           selection range))

      ;; Older IE fallback
      (let [range (.createTextRange (.-body doc))]
        (.moveToElementText range text)
        (.select range)))))

;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;;

(defn compare-descending
  "Returns a comparator that results in the opposite order of
  `compare`."
  [a b]
  (compare b a))

(defn px
  "Formats a number as a CSS pixel measurement"
  [n]
  (str n "px"))

(defn scroll-when
  [key scroll-cell]
  (cell= (with-let [s (= scroll-cell key)]
           (when s
             (with-timeout 0
               (~(partial reset! scroll-cell) nil))))))

(defn nil-or-zero?
  "Checks if value is nil, 0 or \"0\""
  [v]
  (contains? #{nil 0 "0"} v))

(defn ensure-boolean
  "Ensures boolean value from either a boolean or a boolean string."
  [ensuring-boolean]
  (-> ensuring-boolean
      str
      str/lower-case
      parse-boolean))

(defn ensure-js-date
  "Ensures a valid js/Date if inputs permit it."
  [date]
  (let [date-from-epoch  (try (. DateTime fromMillis date) (catch js/Error _ nil))
        date-from-jsdate (try (. DateTime fromJSDate date) (catch js/Error _ nil))
        date-from-string (try (. DateTime fromISO    date) (catch js/Error _ nil))]
    (cond (and date-from-epoch
              (. date-from-epoch -isValid))
          (. date-from-epoch toJSDate)

          (and date-from-jsdate
              (. date-from-jsdate -isValid))
          (. date-from-jsdate toJSDate)
          
          (and date-from-string
              (. date-from-string -isValid))
          (. date-from-string toJSDate)
          
          :else
          nil)))

(defn date-in-past?
  "Determines if date specified is in the past."
  [new-date]
  (when (not (nil? new-date))
    (let [now            (.now DateTime)
          {:keys [date]} (try
                           {:date (.fromJSDate DateTime (ensure-js-date new-date))}
                           (catch js/Error _ {:error :parse-error}))]
      (when date (< date now)))))
